import type {
	MarketAutoPaymentOut,
	PaymentState,
	SystemAutoPaymentOut,
} from "@/api/custom_types";
import { useTranslation } from "@/hooks";
import {
	useCreatePayment,
	useGetDashboardCommon,
	useGetEurUsdRate,
	useGetRelations,
	useGetUserPaymentInfo,
	useRelation,
} from "@/services";
import { currentUserAtom } from "@/store/login";
import { formatNumber } from "@/utils/tools";
import type { TranslationF } from "@/utils/type-utils";
import { zodResolver } from "@hookform/resolvers/zod";
import {
	Button,
	Dialog,
	DialogContent,
	DialogHeader,
	DialogScrollableArea,
	DialogTitle,
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
	Icon,
	Input,
	SmartSelect,
	Typography,
} from "@st-marketplace/ui";
import { useAtomValue } from "jotai";
import { type FC, type ReactNode, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import PaymentMethodPicker from "./PaymentMethodPicker";
import PaymentResultStep from "./PaymentResultStep";
import UsdtTransferStep from "./UsdtTransferStep";
import {
	PAYMENT_METHODS as METHODS,
	buildPaymentBody,
	eurToUsdt,
	getOutstanding,
	isUsdtPayable,
} from "./paymentForm";

/**
 * One form for every payment a user can make:
 *  - to a counterparty (buyer -> seller, or seller -> buyer), picked from their relations
 *  - to the platform, to pay off the system debt (`target="platform"`)
 *
 * The direction, the allowed methods and the recipient wallet all follow from the
 * selected relation, so the user only answers: who, how much, how.
 *
 * Every slot is always rendered at a fixed height, so choosing a recipient or
 * switching method fills the form in instead of resizing it.
 */

const getSchema = (t: TranslationF, needsRelation: boolean) =>
	z.object({
		relationId: needsRelation
			? z.string().min(1, t("validation.required"))
			: z.string().optional(),
		method: z.enum(METHODS),
		amount: z.coerce.number().positive(t("payment.form.amountInvalid")),
		note: z.string().nullable().optional(),
	});

type FormData = z.infer<ReturnType<typeof getSchema>>;

interface AddPaymentDialogProps {
	relationId?: string;
	/**
	 * "send": money you are paying out - as a buyer to your seller, and the only
	 * direction that can settle on-chain, since USDT goes to the seller's wallet.
	 *
	 * "receive": money a buyer has already paid you. The backend accepts these
	 * immediately (the seller is the one confirming), so they only ever record a
	 * manual transfer.
	 */
	direction?: "send" | "receive";
	/** "platform" pays off the system debt of the signed-in user. */
	target?: "counterparty" | "platform";
	size?: "sm" | "md" | "lg";
	color?: "primary" | "secondary";
	trigger?: (open: () => void) => ReactNode;
}

const AddPaymentDialog: FC<AddPaymentDialogProps> = ({
	direction = "send",
	target = "counterparty",
	size,
	color = "primary",
	trigger,
	relationId,
}) => {
	const { t } = useTranslation();
	const me = useAtomValue(currentUserAtom);
	const isPlatform = target === "platform";
	const isSending = direction === "send" || isPlatform;

	const [isOpen, setIsOpen] = useState(false);
	/**
	 * The flow stays in one dialog: filling the form, transferring the USDT and
	 * the receipt are steps of the same window, not three windows in a row.
	 */
	const [step, setStep] = useState<"form" | "transfer" | "result">("form");
	const [result, setResult] = useState<PaymentState>("pending");
	const [autoPayment, setAutoPayment] = useState<
		SystemAutoPaymentOut | MarketAutoPaymentOut | null
	>(null);
	/** Once the user picks a method themselves, stop steering it for them. */
	const [methodPicked, setMethodPicked] = useState(false);
	/** Same for the amount: a typed number wins over any prefill. */
	const [amountEdited, setAmountEdited] = useState(false);
	/** What was filed, kept for the receipt after the form resets. */
	const [filed, setFiled] = useState<{ amount: string; user: string } | null>(
		null,
	);

	const createPayment = useCreatePayment();

	const form = useForm<FormData>({
		resolver: zodResolver(getSchema(t, !isPlatform)),
		defaultValues: { relationId: relationId ?? "", method: "usdt", amount: 0 },
	});
	const { method, relationId: selectedId } = form.watch();

	const { data: relations, isLoading: relationsListLoading } = useGetRelations(
		{ query: { statuses: ["ACTIVE"], page: 1, page_size: 100 } },
		{ enabled: !isPlatform && isOpen },
	);
	const { data: preselectedRelation, isLoading: preselectedRelationLoading } =
		useRelation(
			{ path: { relation_id: relationId ?? "" } },
			{ enabled: !isPlatform && isOpen && !!relationId },
		);
	const relationsLoading = relationsListLoading || preselectedRelationLoading;
	const { data: common } = useGetDashboardCommon(
		{ query: { user_id: me?.id } },
		{ enabled: isPlatform },
	);

	/** Sending pays the sellers you buy from; receiving records what your buyers paid. */
	const myRelations = useMemo(
		() =>
			[
				...(preselectedRelation ? [preselectedRelation] : []),
				...(relations?.results ?? []).filter(
					(r) => r.id !== preselectedRelation?.id,
				),
			].filter((r) =>
				isSending ? r.buyer_id === me?.id : r.seller_id === me?.id,
			),
		[relations, preselectedRelation, me?.id, isSending],
	);

	const relation = useMemo(
		() => myRelations.find((r) => r.id === selectedId) ?? null,
		[myRelations, selectedId],
	);

	// Only the buyer can settle with USDT (it goes to the seller's registered wallet).
	const iAmBuyer = !!relation && relation.buyer_id === me?.id;
	const counterpartyId = relation
		? iAmBuyer
			? relation.seller_id
			: relation.buyer_id
		: undefined;

	const { data: paymentInfo, isFetching: walletLoading } =
		useGetUserPaymentInfo(
			{ path: { user_id: counterpartyId! } },
			{ enabled: !!counterpartyId && iAmBuyer },
		);

	const recipientWallet = isPlatform
		? common?.system_usdt_trc_wallet
		: paymentInfo?.find((info) => info.type === "automatic")?.wallet;

	const usdtAvailable = isUsdtPayable({
		isPlatform,
		iAmBuyer,
		recipientWallet,
	});

	/** What the user still owes, in EUR - every balance in the system is EUR. */
	const outstandingEur = isPlatform
		? Math.max(Number(common?.platform_debts ?? 0), 0)
		: relation
			? getOutstanding(relation.current_debt)
			: 0;

	const isUsdt = method === "usdt";
	const {
		data: eurUsd,
		isLoading: rateLoading,
		isError: rateFailed,
	} = useGetEurUsdRate(isOpen);
	const rate = Number(eurUsd?.rate);
	const outstandingUsdt = eurToUsdt(outstandingEur, rate);
	/** Waiting on the rate, as opposed to knowing it is unavailable. */
	const awaitingRate = isUsdt && rateLoading && !rateFailed;

	/** The same debt, in the currency the chosen method actually moves. */
	const outstanding = isUsdt ? outstandingUsdt : outstandingEur;

	// Steer towards USDT while the user hasn't chosen, but never while the wallet
	// lookup is still in flight - that used to drop everyone onto a manual method.
	useEffect(() => {
		if (methodPicked || walletLoading) return;
		form.setValue("method", usdtAvailable ? "usdt" : "bank_transfer");
	}, [usdtAvailable, walletLoading, methodPicked, form]);

	// Paying off the balance is the common case, so it is filled in - but only
	// while the amount is still ours. Once the user types, a late exchange rate
	// or a method switch must not overwrite what they chose.
	useEffect(() => {
		if (amountEdited) return;
		form.setValue("amount", outstanding && outstanding > 0 ? outstanding : 0);
	}, [outstanding, amountEdited, form]);

	const counterpartyName = relation
		? iAmBuyer
			? relation.seller_display_name
			: relation.buyer_display_name
		: "";
	const recipientName = isPlatform
		? t("payment.form.platformRecipient")
		: counterpartyName;

	const relationItems = useMemo(
		() =>
			myRelations
				.map((r) => {
					const asBuyer = r.buyer_id === me?.id;
					const owed = getOutstanding(r.current_debt);
					return {
						value: r.id,
						label: asBuyer ? r.seller_display_name : r.buyer_display_name,
						owed,
						counter: owed > 0 ? formatNumber(owed, "price") : undefined,
					};
				})
				// Biggest balance first: that's the one you're most likely settling.
				.sort((a, b) => b.owed - a.owed),
		[myRelations, me?.id],
	);

	const currency = isUsdt ? t("payment.form.usdt") : t("payment.form.eur");
	const amount = Number(form.watch("amount")) || 0;
	const ready = isPlatform || !!relation;

	const methodHint = !ready
		? t("payment.form.emptyState")
		: isUsdt
			? outstandingUsdt === null
				? t("payment.form.rateUnavailable")
				: t("payment.form.usdtHint", {
						rate: rate.toFixed(3),
						eur: formatNumber(outstandingEur, "price"),
						usdt: outstandingUsdt,
					})
			: !isSending
				? t("payment.form.receiveHint")
				: !usdtAvailable
					? t("payment.form.noWallet", { user: counterpartyName })
					: t("payment.form.manualHint");

	const close = () => {
		setIsOpen(false);
		setMethodPicked(false);
		setAmountEdited(false);
		setStep("form");
		setAutoPayment(null);
		form.reset({
			relationId: relationId ?? "",
			method: "usdt",
			amount: 0,
			note: null,
		});
	};

	const handleSubmit = form.handleSubmit((data) => {
		createPayment.mutate(
			{
				body: buildPaymentBody(data.method, data.amount, data.note, {
					relationId: isPlatform ? null : data.relationId ?? "",
					userId: me?.id ?? "",
					recipientWallet: recipientWallet ?? "",
				}),
			},
			{
				onSuccess: (res) => {
					setFiled({
						amount: isUsdt
							? `${data.amount} ${currency}`
							: formatNumber(data.amount, "price"),
						user: recipientName,
					});
					if (
						res.type === "MARKET_AUTOMATIC" ||
						res.type === "SYSTEM_AUTOMATIC"
					) {
						setAutoPayment(res);
						setStep("transfer");
						return;
					}
					setResult(res.state === "accepted" ? "accepted" : "pending");
					setStep("result");
				},
			},
		);
	});

	return (
		<>
			{trigger ? (
				trigger(() => setIsOpen(true))
			) : (
				<Button
					type="button"
					color={color}
					size={size}
					className="normal-case flex-1"
					// Money, not a paper plane: a send icon read as "message".
					startIcon={
						<Icon
							name={isSending ? "money-dollar-circle-line" : "hand-coin-line"}
						/>
					}
					onClick={() => setIsOpen(true)}
				>
					{isSending ? t("payment.form.title") : t("payment.form.receiveTitle")}
				</Button>
			)}

			<Dialog
				open={isOpen}
				onOpenChange={(o) => (o ? setIsOpen(true) : close())}
			>
				<DialogContent className="w-full sm:max-w-lg">
					<DialogHeader>
						<DialogTitle className="flex items-center gap-2">
							<Icon
								name="money-dollar-circle-line"
								className="text-primary-100"
							/>
							{step === "transfer"
								? t("payment.add.autoDialog.title")
								: isPlatform
									? t("payment.form.platformTitle")
									: isSending
										? t("payment.form.title")
										: t("payment.form.receiveTitle")}
						</DialogTitle>
						{step === "form" && (
							<Typography
								variant="body-sm"
								className="mt-1 text-start text-secondary-text-100"
							>
								{isPlatform
									? t("payment.form.platformSubtitle")
									: isSending
										? t("payment.form.subtitle")
										: t("payment.form.receiveSubtitle")}
							</Typography>
						)}
					</DialogHeader>

					{/* One height for every step, so the window never jumps. */}
					<div className="flex min-h-128 flex-col">
						{step === "form" && (
							<Form {...form}>
								<form
									noValidate
									onSubmit={handleSubmit}
									className="flex flex-1 flex-col"
								>
									<DialogScrollableArea className="flex max-h-[60vh] sm:max-h-[70vh] flex-1 flex-col gap-6 pt-0">
										{/* 1. Who gets paid, and what is still open with them. */}
										<div className="flex flex-col gap-2">
											<FormLabel required={!isPlatform}>
												{isSending
													? t("payment.form.recipient")
													: t("payment.form.payer")}
											</FormLabel>
											{isPlatform ? (
												<div className="flex h-12 items-center gap-2 rounded-xl border border-secondary-border bg-background-100 px-3">
													<Icon
														name="building-line"
														className="text-primary-100"
													/>
													<Typography variant="body-md">
														{recipientName}
													</Typography>
												</div>
											) : (
												<FormField
													control={form.control}
													name="relationId"
													render={({ field }) => (
														<FormItem>
															<FormControl>
																<SmartSelect
																	items={relationItems}
																	value={field.value || ""}
																	onChange={field.onChange}
																	searchable
																	loading={relationsLoading}
																	hasTitlePrefix
																	leadingIcon={<Icon name="user-line" />}
																	placeholder={
																		isSending
																			? t("payment.form.recipient")
																			: t("payment.form.payer")
																	}
																	searchPlaceholder={t(
																		"payment.form.recipientPlaceholder",
																	)}
																	// The kit's popover defaults to "min-w-56
																	// max-w-80", narrower than this field.
																	popoverProps={{
																		className:
																			"w-[var(--radix-popover-trigger-width)] max-w-none p-0",
																	}}
																/>
															</FormControl>
															<FormMessage />
														</FormItem>
													)}
												/>
											)}
											{/* Reserved line: the relationship and the open balance. */}
											<div className="flex h-5 items-center justify-between gap-4">
												<Typography
													variant="body-sm"
													className="truncate text-secondary-text-100"
												>
													{ready
														? isPlatform
															? t("payment.form.roleSystem")
															: iAmBuyer
																? t("payment.form.roleBuyer")
																: t("payment.form.roleSeller")
														: ""}
												</Typography>
												{ready && (
													<Typography
														variant="body-sm"
														className="shrink-0 tabular-nums text-secondary-text-100"
													>
														{t("payment.form.outstanding")}:{" "}
														<span className="font-semibold text-secondary-text">
															{formatNumber(outstandingEur, "price")}
														</span>
													</Typography>
												)}
											</div>
										</div>

										{/* 2. How much - the one number this form is about. */}
										<FormField
											control={form.control}
											name="amount"
											render={({ field }) => (
												<FormItem>
													<FormLabel required>
														{t("payment.form.amount")}
													</FormLabel>
													{awaitingRate ? (
														// The USDT amount is a converted EUR balance, so
														// until the rate lands there is no number to show.
														// Saying so beats a 0 that then jumps.
														<div className="flex h-10 items-center gap-2 rounded-sm border border-secondary-text-disabled px-2">
															<Icon
																name="loader-4-line"
																size="sm"
																className="animate-spin text-secondary-icon"
															/>
															<Typography
																variant="body-sm"
																className="text-secondary-text-100"
															>
																{t("payment.form.loadingRate")}
															</Typography>
														</div>
													) : (
														<FormControl>
															{/* text + inputMode, not type=number: the spinner
														  arrows sit on top of the currency suffix. */}
															<Input
																type="text"
																inputMode="decimal"
																ltr
																size="lg"
																disabled={!ready}
																append={currency}
																className="text-lg tabular-nums"
																value={field.value ?? ""}
																onChange={(e) => {
																	setAmountEdited(true);
																	field.onChange(
																		e.target.value.replace(/[^\d.]/g, ""),
																	);
																}}
															/>
														</FormControl>
													)}
													<div className="flex h-5 items-center justify-between gap-4">
														<FormMessage />
														{!!outstanding &&
															outstanding > 0 &&
															amount !== outstanding && (
																<button
																	type="button"
																	className="ms-auto text-primary-100 text-sm underline-offset-2 hover:underline"
																	onClick={() => {
																		// back under our control again
																		setAmountEdited(false);
																		form.setValue("amount", outstanding, {
																			shouldValidate: true,
																		});
																	}}
																>
																	{t("payment.form.payFull", {
																		amount: isUsdt
																			? `${outstanding} ${currency}`
																			: formatNumber(outstanding, "price"),
																	})}
																</button>
															)}
													</div>
												</FormItem>
											)}
										/>

										{/* 3. How it is paid, plus the one detail that method needs. */}
										<FormField
											control={form.control}
											name="method"
											render={({ field }) => (
												<FormItem>
													<FormLabel>
														{isSending
															? t("payment.form.method.label")
															: t("payment.form.method.receivedLabel")}
													</FormLabel>
													<PaymentMethodPicker
														value={field.value}
														disabled={!ready}
														usdtAvailable={usdtAvailable}
														onChange={(value) => {
															setMethodPicked(true);
															field.onChange(value);
														}}
													/>
													<Typography
														variant="body-sm"
														className="flex min-h-10 items-center text-secondary-text-100"
													>
														{methodHint}
													</Typography>
												</FormItem>
											)}
										/>

										{/* Reserved detail slot: wallet for USDT, reference for manual. */}
										<div className="min-h-24">
											{ready && isUsdt ? (
												<div className="rounded-xl border border-secondary-border bg-background-100 p-4">
													<div className="flex items-center gap-2">
														<Icon
															name="wallet-line"
															className="text-primary-100"
														/>
														<Typography
															variant="caption"
															className="text-secondary-text-100"
														>
															{t("payment.form.walletOf", {
																user: recipientName,
															})}
														</Typography>
													</div>
													<Typography
														variant="body-sm"
														className="mt-2 break-all"
														dir="ltr"
													>
														{recipientWallet}
													</Typography>
												</div>
											) : (
												<FormField
													control={form.control}
													name="note"
													render={({ field }) => (
														<FormItem>
															<FormLabel>{t("payment.form.note")}</FormLabel>
															<FormControl>
																<Input
																	{...field}
																	disabled={!ready}
																	value={field.value ?? ""}
																	placeholder={t(
																		"payment.form.notePlaceholder",
																	)}
																/>
															</FormControl>
														</FormItem>
													)}
												/>
											)}
										</div>
									</DialogScrollableArea>

									<div className="flex flex-col-reverse gap-2 border-secondary-border border-t px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
										<Typography
											variant="body-sm"
											className="text-secondary-text-100"
										>
											{ready && amount > 0
												? t(
														isSending
															? "payment.form.summary"
															: "payment.form.summaryReceive",
														{
															amount: isUsdt
																? `${amount} ${currency}`
																: formatNumber(amount, "price"),
															user: recipientName,
														},
													)
												: ""}
										</Typography>
										<div className="flex flex-col-reverse gap-2 sm:flex-row">
											<Button
												type="button"
												color="secondary"
												size="lg"
												onClick={close}
											>
												{t("common.button.cancel")}
											</Button>
											<Button
												type="submit"
												color="primary"
												size="lg"
												className="normal-case"
												disabled={
													!ready ||
													amount <= 0 ||
													awaitingRate ||
													// USDT settles onto a specific address: never send
													// one while the wallet is unknown or still loading.
													(isUsdt && (!recipientWallet || walletLoading))
												}
												loading={createPayment.isPending}
											>
												{isUsdt
													? t("payment.form.submitUsdt")
													: isSending
														? t("payment.form.submitManual")
														: t("payment.form.submitReceive")}
											</Button>
										</div>
									</div>
								</form>
							</Form>
						)}

						{step === "transfer" && autoPayment && (
							<UsdtTransferStep
								data={autoPayment}
								secondaryLabel={t("payment.add.autoDialog.buttonLater")}
								onSecondary={close}
								onConfirmed={() => {
									setResult("accepted");
									setStep("result");
								}}
							/>
						)}

						{step === "result" && (
							<PaymentResultStep
								state={result}
								amount={filed?.amount}
								user={filed?.user}
								paymentsTab={isSending ? "sent" : "received"}
								onClose={close}
							/>
						)}
					</div>
				</DialogContent>
			</Dialog>
		</>
	);
};

export default AddPaymentDialog;
